/**
 * PPEDetectionResults
 *
 * Design: Safety Compliance Dashboard
 * - High-visibility compliance status
 * - Person-card layout for individual checks
 * - Detailed gear breakdown (Helmet, Vest, Boots, etc.)
 */
import React from "react";
import {
  ShieldCheck,
  AlertOctagon,
  HardHat,
  User,
  CheckCircle2,
  XCircle,
  Download,
  FileVideo,
  Clock,
  Activity,
  Users,
} from "lucide-react";

// ============================================================================
// TYPES
// ============================================================================

interface PPEItem {
  label: string;
  confidence: number;
  xyxy: number[];
}

interface PersonDetection {
  person_box: number[];
  person_confidence: number;
  ppe_items: PPEItem[];
  wearing_required: Record<string, boolean>;
  status: "PROTECTED" | "UNPROTECTED";
}

interface PPEDetectionResultsData {
  type: "ppe_detection" | "ppe_detection_video";
  request_id: string;
  detections?: PersonDetection[];
  metrics?: {
    num_persons: number;
    num_detections: number;
    total_sec: number;
  };
  summary?: any;
  processing_time: number;
  image_base64?: string;
  metadata?: {
    original_filename?: string;
    upload_size?: number;
    frames: number;
    processing_seconds: number;
  };
  frames?: Array<{
    frame: number;
    num_persons: number;
    num_detections: number;
  }>;
}

interface PPEVideoResultsProps {
  results: PPEDetectionResultsData;
  annotatedOutputUrl?: string;
  inputFileName?: string;
}

interface PPEDetectionResultsProps {
  results: PPEDetectionResultsData;
  annotatedOutputUrl?: string;
  inputFileName?: string;
}

// ============================================================================
// VIDEO COMPONENT
// ============================================================================

const PPEVideoResults: React.FC<PPEVideoResultsProps> = ({
  results,
  annotatedOutputUrl,
  inputFileName,
}) => {
  return (
    <div className="space-y-6">
      {/* Simple video header */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-primary-500/20 rounded-xl">
            <FileVideo className="w-6 h-6 text-primary-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Video Analysis</h2>
            <p className="text-slate-400 text-sm">{inputFileName}</p>
          </div>
        </div>

        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2 text-slate-400">
            <Clock className="w-4 h-4" />
            {results.metadata?.processing_seconds.toFixed(1)}s processing
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Activity className="w-4 h-4" />
            {results.summary?.total_frames} frames
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Video Player */}
        <div className="lg:col-span-2 bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <div className="rounded-xl overflow-hidden bg-black aspect-video">
            {annotatedOutputUrl && (
              <video controls className="w-full h-full">
                <source src={annotatedOutputUrl} type="video/mp4" />
              </video>
            )}
          </div>
        </div>

        {/* Stats Sidebar */}
        <div className="space-y-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary-400" /> Detection Summary
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400">Total Persons</span>
                  <span className="text-white font-mono">
                    {results.summary?.total_persons_detected}
                  </span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div className="bg-primary-500 w-full h-full"></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400">Total Detections</span>
                  <span className="text-white font-mono">
                    {results.summary?.total_detections}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const PPEDetectionResults: React.FC<PPEDetectionResultsProps> = ({
  results,
  annotatedOutputUrl,
  inputFileName,
}) => {
  const isVideoResult = results.type === "ppe_detection_video";

  if (isVideoResult) {
    return (
      <PPEVideoResults
        results={results}
        annotatedOutputUrl={annotatedOutputUrl}
        inputFileName={inputFileName}
      />
    );
  }

  // IMAGE RESULTS
  const summary = results.summary || {
    total_persons: results.detections?.length || 0,
    protected_count:
      results.detections?.filter((d) => d.status === "PROTECTED").length || 0,
    unprotected_count:
      results.detections?.filter((d) => d.status === "UNPROTECTED").length || 0,
  };

  const isFullyCompliant =
    summary.unprotected_count === 0 && summary.total_persons > 0;

  // Image source
  const imageSrc =
    annotatedOutputUrl ||
    (results.image_base64
      ? `data:image/png;base64,${results.image_base64}`
      : null);

  const handleDownloadReport = () => {
    // Basic text report generation
    const lines = [
      `PPE ANALYSIS REPORT`,
      `Date: ${new Date().toISOString()}`,
      `Status: ${isFullyCompliant ? "COMPLIANT" : "NON-COMPLIANT"}`,
      `\nDetails:`,
      ...(results.detections?.map((d, i) => `Person #${i + 1}: ${d.status}`) ||
        []),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "ppe_report.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Hero Status Card */}
      <div
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${
          isFullyCompliant
            ? "from-emerald-500/20 via-slate-800/50 to-slate-900 border-emerald-500/30"
            : "from-red-500/20 via-slate-800/50 to-slate-900 border-red-500/30"
        } border p-8`}
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10">
          <div className="flex items-center gap-6">
            <div
              className={`w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg ${
                isFullyCompliant
                  ? "bg-emerald-500/20 text-emerald-400"
                  : "bg-red-500/20 text-red-400"
              }`}
            >
              {isFullyCompliant ? (
                <ShieldCheck className="w-10 h-10" />
              ) : (
                <AlertOctagon className="w-10 h-10" />
              )}
            </div>
            <div>
              <p className="text-slate-400 text-sm font-medium mb-1">
                Safety Compliance Status
              </p>
              <h2
                className={`text-4xl font-bold ${
                  isFullyCompliant ? "text-emerald-400" : "text-red-400"
                }`}
              >
                {isFullyCompliant ? "Fully Compliant" : "Non-Compliant"}
              </h2>
              <p className="text-slate-300 text-sm mt-2">
                {summary.unprotected_count} personnel detected without required
                safety gear.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 text-center min-w-[100px]">
              <p className="text-3xl font-bold text-white">
                {summary.total_persons}
              </p>
              <p className="text-slate-400 text-xs">Total Persons</p>
            </div>
            <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 text-center min-w-[100px]">
              <p className="text-3xl font-bold text-red-400">
                {summary.unprotected_count}
              </p>
              <p className="text-slate-400 text-xs">Violations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visual Evidence */}
        {imageSrc && (
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Detection Evidence</h3>
              <button
                onClick={handleDownloadReport}
                className="text-xs flex items-center gap-1 text-cyan-400 hover:text-cyan-300"
              >
                <Download className="w-3 h-3" /> Download Report
              </button>
            </div>
            <div className="rounded-xl overflow-hidden border border-slate-700/50 bg-slate-900/50">
              <img
                src={imageSrc}
                alt="Analysis"
                className="w-full object-contain max-h-[500px]"
              />
            </div>
          </div>
        )}

        {/* Personnel List */}
        <div className="space-y-4">
          {results.detections?.map((person, idx) => (
            <div
              key={idx}
              className={`rounded-xl p-5 border ${
                person.status === "PROTECTED"
                  ? "bg-slate-800/50 border-emerald-500/20"
                  : "bg-red-500/5 border-red-500/30"
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-lg ${
                      person.status === "PROTECTED"
                        ? "bg-emerald-500/20"
                        : "bg-red-500/20"
                    }`}
                  >
                    <User
                      className={`w-5 h-5 ${
                        person.status === "PROTECTED"
                          ? "text-emerald-400"
                          : "text-red-400"
                      }`}
                    />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">
                      Person #{idx + 1}
                    </h4>
                    <p
                      className={`text-xs font-bold ${
                        person.status === "PROTECTED"
                          ? "text-emerald-400"
                          : "text-red-400"
                      }`}
                    >
                      {person.status}
                    </p>
                  </div>
                </div>
                <span className="text-slate-500 text-xs bg-slate-900 px-2 py-1 rounded">
                  {(person.person_confidence * 100).toFixed(0)}% Conf
                </span>
              </div>

              {/* Gear Status */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(person.wearing_required).map(
                  ([item, wearing]) => (
                    <div
                      key={item}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        wearing
                          ? "bg-slate-900/50"
                          : "bg-red-500/10 border border-red-500/20"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {item.toLowerCase().includes("helmet") && (
                          <HardHat className="w-4 h-4 text-slate-400" />
                        )}
                        <span
                          className={
                            wearing ? "text-slate-300" : "text-red-300"
                          }
                        >
                          {item}
                        </span>
                      </div>
                      {wearing ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
